import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Plus, Trash2, Pencil, Calculator, Truck, Train, Plane, Ship, Package, MapPin, Clock, X, Lightbulb, Zap, Download, BookOpen, ChevronDown, ChevronUp, Info, Navigation, Shield, Upload } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, AreaChart, Area, ComposedChart, RadialBarChart, RadialBar, Treemap } from 'recharts';
import { format, subDays, subMonths, subYears, parseISO, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import '@/App.css';

// Auth components
import { AuthProvider, useAuth } from './components/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute';
import UsageWidget from './components/UsageWidget';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailNotice from './pages/VerifyEmailNotice';
import GoogleCallback from './pages/GoogleCallback';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AdminDashboard from './pages/AdminDashboard';
import CommuteSurveyPublic from './pages/CommuteSurveyPublic';
import ScopeThree from './pages/ScopeThree';
import Shanthi from './pages/Shanthi';
import Dashboard from './pages/Dashboard';
import ShipmentNew from './pages/ShipmentNew';
import ShipmentsBulk from './pages/ShipmentsBulk';
import ScopeThreeActivityForm from './components/scope3/ActivityForm';
import { setupInterceptors } from './services/tokenService';
import DashboardContent from './components/DashboardContent';
import AgentUpload from './components/agent/AgentUpload';
import ChatBot from './components/ChatBot';

// Setup axios interceptors once
setupInterceptors();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette for charts
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

// Loading Component
const LoadingSpinner = () => (
  <div className="loading-overlay">
    <div className="spinner-container">
      <div className="spinner"></div>
      <p className="spinner-text">Calculating emissions...</p>
    </div>
  </div>
);

// Main Calculator Component
export const TransportCalculator = ({ embedded = false, showBulkTab = true, showDashboard = true } = {}) => {
  const { user, logout } = useAuth();

  const [currentGood, setCurrentGood] = useState({
    name: '',
    quantity: '',
    unit: 'kg',
    ghg_category: 'upstream'
  });

  // Centralised GHG Protocol category. Drives form body in the Single-Shipment
  // tab and the required columns in the Bulk-Upload tab.
  // 4 = Upstream transport, 9 = Downstream transport, 6 = Business travel, 7 = Employee commute.
  const [scope3Category, setScope3Category] = useState(4);

  // Chat prefill for the scope 3 activity form (Cat 6 / 7)
  const [scope3ChatPrefill, setScope3ChatPrefill] = useState(null);

  // Bump this whenever a Scope 3 activity is saved. DashboardContent listens
  // and refetches /api/scope3/activities so Cat 6/7 saves reflect immediately.
  const [scope3RefreshTick, setScope3RefreshTick] = useState(0);

  const [transportLegs, setTransportLegs] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [useManualDistance, setUseManualDistance] = useState(false);
  const [currentLeg, setCurrentLeg] = useState({
    from_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
    to_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
    transport_mode: '',
    vehicle_type: '',
    cost_type: 'total',
    cost_value: '',
    manual_distance: null,
    trips: '1'
  });

  const [locationSearch, setLocationSearch] = useState({
    from: '',
    to: ''
  });

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleTypeData, setVehicleTypeData] = useState(null); // Full hierarchical data from API
  const [searchResults, setSearchResults] = useState({
    from: [],
    to: []
  });

  // Hierarchical vehicle selection state (Calculator Mode)
  const [vehicleCategory, setVehicleCategory] = useState(''); // van, hgv, hgv_refrigerated (road) or tanker, cargo (water)
  const [fuelType, setFuelType] = useState(''); // diesel, petrol, cng (vans only)
  const [sizeClass, setSizeClass] = useState(''); // class_i, class_ii, etc (vans) or vehicle sizes
  const [ladenStatus, setLadenStatus] = useState('50_laden'); // 0_laden, 50_laden, 100_laden, average
  const [flightType, setFlightType] = useState(''); // domestic, short_haul, long_haul, international
  const [includeRadiativeForcing, setIncludeRadiativeForcing] = useState(true);
  const [shipType, setShipType] = useState(''); // crude, products, chemical, etc
  const [shipSize, setShipSize] = useState(''); // specific size option

  const [loading, setLoading] = useState(false);
  const [addingLeg, setAddingLeg] = useState(false);
  const [showCalculateButton, setShowCalculateButton] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [calculationResult, setCalculationResult] = useState(null);


  // Guide visibility state
  const [showGuide, setShowGuide] = useState(true);

  // Tab state for programmatic switching
  const [activeTab, setActiveTab] = useState('bulk-upload');

  // Transport mode icons
  const transportIcons = {
    road: Truck,
    rail: Train,
    air: Plane,
    water: Ship
  };

  const transportModes = [
    { value: 'road', label: 'Road Transport' },
    { value: 'rail', label: 'Railway' },
    { value: 'air', label: 'Airways' },
    { value: 'water', label: 'Seaways' }
  ];

  // Load shipments when user is authenticated
  useEffect(() => {
    const controller = new AbortController();
    const fetchShipments = async () => {
      if (user) {
        try {
          setLoadingShipments(true);
          const response = await axios.get(`${API}/shipments`, { signal: controller.signal });
          setShipments(response.data);
        } catch (error) {
          if (!axios.isCancel(error)) {
            console.error('Error loading shipments:', error);
          }
        } finally {
          setLoadingShipments(false);
        }
      } else {
        // Clear shipments when user logs out
        setShipments([]);
      }
    };
    fetchShipments();
    return () => controller.abort();
  }, [user]); // Re-run when user logs in/out

  // Check if calculate button should be shown
  useEffect(() => {
    setShowCalculateButton(
      currentGood.name &&
      currentGood.quantity &&
      currentGood.ghg_category &&
      transportLegs.length > 0
    );
  }, [currentGood, transportLegs]);

  const loadShipments = async () => {
    try {
      setLoadingShipments(true);
      const response = await axios.get(`${API}/shipments`);
      setShipments(response.data);
    } catch (error) {
      console.error('Error loading shipments:', error);
    } finally {
      setLoadingShipments(false);
    }
  };

  const searchLocations = async (query, locationType = 'general') => {
    if (query.length < 1) return [];

    try {
      const endpoint = locationType === 'airport'
        ? `${API}/airports/search?query=${encodeURIComponent(query)}`
        : `${API}/locations/search?query=${encodeURIComponent(query)}&location_type=${locationType}`;

      const response = await axios.get(endpoint);

      if (locationType === 'airport') {
        return response.data.slice(0, 6).map(airport => ({
          address: `${airport.name} (${airport.code}) - ${airport.city}, ${airport.country}`,
          latitude: airport.latitude,
          longitude: airport.longitude,
          type: 'airport'
        }));
      }

      return response.data.slice(0, 6);
    } catch (error) {
      console.error('Location search error:', error);
      return [];
    }
  };

  const handleLocationSearch = async (field, query) => {
    setLocationSearch(prev => ({ ...prev, [field]: query }));

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.length >= 2) {
      // Debounce search with 150ms delay for instant feel
      const newTimeout = setTimeout(async () => {
        const locationType = currentLeg.transport_mode === 'air' ? 'airport' : 'general';
        try {
          const results = await searchLocations(query, locationType);
          setSearchResults(prev => ({
            ...prev,
            [field]: Array.isArray(results) ? results : []
          }));
        } catch (error) {
          if (!error.response || error.response.status >= 500) {
            toast.error('Location search failed. Please try again.');
          }
          setSearchResults(prev => ({ ...prev, [field]: [] }));
        }
      }, 150);

      setSearchTimeout(newTimeout);
    } else {
      setSearchResults(prev => ({ ...prev, [field]: [] }));
    }
  };

  const selectLocation = (field, location) => {
    setCurrentLeg(prev => ({
      ...prev,
      [`${field}_location`]: location
    }));
    setLocationSearch(prev => ({ ...prev, [field]: location.address }));
    setSearchResults(prev => ({ ...prev, [field]: [] }));
  };


  const loadVehicleTypes = async (transportMode) => {
    try {
      const response = await axios.get(`${API}/vehicle-types/${transportMode}`);
      const data = response.data;

      setVehicleTypeData(data);
      // Reset vehicle selection state
      setVehicleCategory('');
      setFuelType('');
      setSizeClass('');
      setLadenStatus('50_laden');
      setFlightType('');
      setIncludeRadiativeForcing(true);
      setShipType('');
      setShipSize('');

      // Keep old vehicleTypes for backwards compatibility
      setVehicleTypes(data.vehicle_types || []);
    } catch (error) {
      console.error('Error loading vehicle types:', error);
      toast.error('Could not load vehicle types. Please try again.');
      setVehicleTypes([]);
      setVehicleTypeData(null);
    }
  };

  // Helper function to build the vehicle_type key for the API
  const buildVehicleTypeKey = (mode) => {
    if (mode === 'rail') {
      return 'freight_train';
    }

    if (mode === 'air') {
      if (!flightType) return '';
      const rfSuffix = includeRadiativeForcing ? 'with_rf' : 'without_rf';
      return `${flightType}_${rfSuffix}`;
    }

    if (mode === 'road') {
      if (vehicleCategory === 'van') {
        if (!fuelType || !sizeClass) return '';
        return `van_${fuelType}_${sizeClass}`;
      }
      if (vehicleCategory === 'hgv' || vehicleCategory === 'hgv_refrigerated') {
        if (!sizeClass || !ladenStatus) return '';
        if (vehicleCategory === 'hgv_refrigerated') {
          return `hgv_refrigerated_${sizeClass}_${ladenStatus}`;
        }
        return `hgv_${sizeClass}_${ladenStatus}`;
      }
      return '';
    }

    if (mode === 'water') {
      if (!vehicleCategory || !shipType || !shipSize) return '';
      return `${vehicleCategory}_${shipType}_${shipSize}`;
    }

    return '';
  };

  useEffect(() => {
    if (currentLeg.transport_mode) {
      loadVehicleTypes(currentLeg.transport_mode);
    } else {
      setVehicleTypes([]);
      setVehicleTypeData(null);
    }
  }, [currentLeg.transport_mode]);

  // Update vehicle_type when hierarchical selection changes
  useEffect(() => {
    if (currentLeg.transport_mode) {
      const vehicleTypeKey = buildVehicleTypeKey(currentLeg.transport_mode);
      if (vehicleTypeKey) {
        handleLegChange('vehicle_type', vehicleTypeKey);
      }
    }
  }, [vehicleCategory, fuelType, sizeClass, ladenStatus, flightType, includeRadiativeForcing, shipType, shipSize, currentLeg.transport_mode]);

  const handleGoodChange = (field, value) => {
    setCurrentGood(prev => ({ ...prev, [field]: value }));
  };

  const handleLegChange = (field, value) => {
    setCurrentLeg(prev => ({ ...prev, [field]: value }));
  };

  const addTransportLeg = async () => {
    // Check maximum legs limit
    if (transportLegs.length >= 5) {
      toast.error('Maximum 5 transport legs allowed');
      return;
    }

    // Validate based on whether manual distance mode is enabled
    if (useManualDistance) {
      // Manual mode: only need transport_mode, vehicle_type, cost_value, and manual_distance
      if (!currentLeg.transport_mode || !currentLeg.vehicle_type || !currentLeg.cost_value) {
        toast.error('Please fill in transport mode, vehicle type, and cost');
        return;
      }
      if (!currentLeg.manual_distance) {
        toast.error('Please enter distance manually or switch to auto calculate');
        return;
      }
    } else {
      // Auto mode: need locations as well
      const missingFields = [];
      if (!currentLeg.transport_mode) missingFields.push('transport mode');
      if (!currentLeg.vehicle_type) missingFields.push('vehicle type');
      if (!currentLeg.from_location.address) missingFields.push('origin (select from suggestions)');
      if (!currentLeg.to_location.address) missingFields.push('destination (select from suggestions)');
      if (!currentLeg.cost_value) missingFields.push('cost');
      if (missingFields.length > 0) {
        toast.error(`Please fill in: ${missingFields.join(', ')}`);
        return;
      }
    }

    // Show loading state while calculating distance
    setAddingLeg(true);
    try {
      // Calculate distance
      let distance = null;
      if (useManualDistance && currentLeg.manual_distance) {
        distance = parseFloat(currentLeg.manual_distance);
      } else {
        const distanceResponse = await axios.post(`${API}/calculate-distance`, {
          from_location: currentLeg.from_location,
          to_location: currentLeg.to_location,
          transport_mode: currentLeg.transport_mode
        });
        distance = distanceResponse.data.distance_km;
      }

      const trips = Math.min(Math.max(Math.floor(Number(currentLeg.trips)) || 1, 1), 1000);
      const newLeg = {
        ...currentLeg,
        distance_km: distance,          // one-way; the backend multiplies by trips
        trips,
        cost_value: parseFloat(currentLeg.cost_value)
      };

      setTransportLegs(prev => [...prev, newLeg]);

      // Reset current leg and clear related states
      setCurrentLeg({
        from_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
        to_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
        transport_mode: '',
        vehicle_type: '',
        cost_type: 'total',
        cost_value: '',
        manual_distance: null,
        trips: '1'
      });
      setLocationSearch({ from: '', to: '' });
      setVehicleTypes([]);
      setVehicleTypeData(null);
      setSearchResults({ from: [], to: [] });
      setUseManualDistance(false);
      // Reset hierarchical vehicle selection
      setVehicleCategory('');
      setFuelType('');
      setSizeClass('');
      setLadenStatus('50_laden');
      setFlightType('');
      setIncludeRadiativeForcing(true);
      setShipType('');
      setShipSize('');

      toast.success(`Transport leg added successfully (${transportLegs.length + 1}/5)`);
    } catch (error) {
      console.error('Error adding transport leg:', error);
      // Check for sea route / port not found errors
      const errorMessage = error.response?.data?.detail || error.message || '';
      if (errorMessage.toLowerCase().includes('port') ||
        errorMessage.toLowerCase().includes('sea') ||
        currentLeg.transport_mode === 'water') {
        toast.error('No nearby port found. Please enter the port name directly or use manual distance.');
      } else {
        toast.error(errorMessage || 'Error adding transport leg. Please check your locations.');
      }
    } finally {
      setAddingLeg(false);
    }
  };

  const removeTransportLeg = (index) => {
    setTransportLegs(prev => prev.filter((_, i) => i !== index));
  };

  // Load a leg into the form for editing, removing it from the list.
  // User edits then clicks "Save & Calculate Route" to re-add with updated values.
  const editTransportLeg = (index) => {
    const leg = transportLegs[index];
    if (!leg) return;
    const hasCoords =
      (leg.from_location?.latitude && leg.to_location?.latitude) || false;
    setCurrentLeg({
      from_location: {
        address: leg.from_location?.address || '',
        latitude: leg.from_location?.latitude || 0,
        longitude: leg.from_location?.longitude || 0,
        type: leg.from_location?.type || 'general',
      },
      to_location: {
        address: leg.to_location?.address || '',
        latitude: leg.to_location?.latitude || 0,
        longitude: leg.to_location?.longitude || 0,
        type: leg.to_location?.type || 'general',
      },
      transport_mode: leg.transport_mode || '',
      vehicle_type: leg.vehicle_type || '',
      cost_type: leg.cost_type || 'total',
      cost_value: leg.cost_value != null ? String(leg.cost_value) : '',
      manual_distance: hasCoords ? null : (leg.distance_km ?? null),
      trips: String(leg.trips || 1),
    });
    setLocationSearch({
      from: leg.from_location?.address || '',
      to: leg.to_location?.address || '',
    });
    setUseManualDistance(!hasCoords && leg.distance_km != null);
    setTransportLegs(prev => prev.filter((_, i) => i !== index));
    toast.info('Leg loaded for editing — adjust fields, then click "Save & Calculate Route" to re-add it.');
    setTimeout(() => {
      document.querySelector('[data-testid="transport-leg-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const calculateShipment = async () => {
    if (!currentGood.name || !currentGood.quantity || !currentGood.ghg_category || transportLegs.length === 0) {
      toast.error('Please add good details and at least one transport leg');
      return;
    }

    setLoading(true);
    try {
      const shipmentData = {
        good: {
          ...currentGood,
          quantity: parseFloat(currentGood.quantity)
        },
        transport_legs: transportLegs
      };

      const response = await axios.post(`${API}/shipments`, shipmentData);
      const shipment = response.data;

      // Store calculation result to display
      setCalculationResult(shipment);
      await loadShipments();

      toast.success('Shipment calculated successfully!');
    } catch (error) {
      console.error('Error calculating shipment:', error);
      const errorMessage = error.response?.data?.detail || 'Error calculating shipment';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load batch-processed rows as legs in the Single Shipment form, then switch tab
  const loadLegsFromBatch = (processedRows) => {
    const usable = (processedRows || []).filter(
      (r) => (r.status === 'success' || r.status === 'warning') && r.shipment_data?.transport_legs?.[0]
    );
    if (usable.length === 0) {
      toast.error('No successful rows to open in Single Shipment.');
      return;
    }
    const MAX_LEGS = 5;
    const trimmed = usable.slice(0, MAX_LEGS);
    if (usable.length > MAX_LEGS) {
      toast.warning(`Single Shipment caps at ${MAX_LEGS} legs — using the first ${MAX_LEGS} of ${usable.length} rows.`);
    }
    const legs = trimmed.map((r) => {
      const src = r.shipment_data.transport_legs[0];
      return {
        from_location: {
          address: src.from_location?.address || r.origin || '',
          latitude: src.from_location?.latitude || 0,
          longitude: src.from_location?.longitude || 0,
          type: src.from_location?.type || 'general',
        },
        to_location: {
          address: src.to_location?.address || r.destination || '',
          latitude: src.to_location?.latitude || 0,
          longitude: src.to_location?.longitude || 0,
          type: src.to_location?.type || 'general',
        },
        transport_mode: src.transport_mode || r.transport_mode || '',
        vehicle_type: src.vehicle_type || r.vehicle_type || '',
        cost_type: src.cost_type || 'total',
        cost_value: Number(src.cost_value || r.cost || 0),
        manual_distance: null,
        distance_km: Number(src.distance_km || r.distance_km || 0),
      };
    });
    // Pull goods info from the first usable row's shipment_data.good
    const firstGood = trimmed[0]?.shipment_data?.good;
    if (firstGood) {
      setCurrentGood({
        name: firstGood.name || '',
        quantity: firstGood.quantity != null ? String(firstGood.quantity) : '',
        unit: firstGood.unit || 'kg',
        ghg_category: firstGood.ghg_category || 'upstream',
      });
    }

    setTransportLegs(legs);
    setActiveTab('calculator');
    const goodsNote = firstGood ? ` and goods from row ${trimmed[0].row_index + 1}` : '';
    toast.success(`Loaded ${legs.length} leg${legs.length > 1 ? 's' : ''}${goodsNote} into Single Shipment.${trimmed.length > 1 ? ' Verify quantity if rows had different weights.' : ''}`);
  };

  // Pre-fill Single Shipment from a chat-extracted shipment proposal.
  // Sets goods + the in-progress leg form (currentLeg) so the user can pick locations
  // from Google Places autocomplete (which provides lat/lng) and then click Save & Calculate Route.
  const prefillFromChatProposal = (s) => {
    if (!s) return;
    // The form only offers kg / tonnes. The chat can propose grams or lbs — convert them here
    // instead of dropping the raw number into a kg field (5000 grams used to become 5000 kg).
    const TO_KG = { grams: 0.001, gram: 0.001, g: 0.001, lbs: 0.45359237, lb: 0.45359237, pounds: 0.45359237 };
    const TONNES = ['tons', 'ton', 'tonne', 'tonnes', 't', 'mt'];
    let unit = (s.unit || 'kg').trim().toLowerCase();
    let quantity = s.quantity != null ? Number(s.quantity) : null;
    if (TO_KG[unit] && quantity != null && !Number.isNaN(quantity)) {
      quantity = Number((quantity * TO_KG[unit]).toFixed(6));
      unit = 'kg';
    } else if (TONNES.includes(unit)) {
      unit = 'tons';
    } else if (unit !== 'kg') {
      // Unknown unit: leave the quantity empty so the user must re-enter it, rather than
      // silently reading it as kg.
      quantity = null;
      unit = 'kg';
    }
    setCurrentGood({
      name: s.good_name || '',
      quantity: quantity != null && !Number.isNaN(quantity) ? String(quantity) : '',
      unit,
      ghg_category: s.ghg_category || 'upstream',
    });
    setCurrentLeg({
      from_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
      to_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
      transport_mode: s.transport_mode || 'road',
      vehicle_type: s.vehicle_type || 'hgv_all_hgvs_average',
      cost_type: 'total',
      cost_value: s.cost != null ? String(s.cost) : '',
      manual_distance: null,
    });
    setLocationSearch({ from: s.origin || '', to: s.destination || '' });
    setUseManualDistance(false);
    setActiveTab('calculator');
    toast.success('Loaded from chat. Pick origin/destination from the suggestions, then click Save & Calculate Route.');
    setTimeout(() => {
      document.querySelector('[data-testid="goods-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const resetCalculation = () => {
    setCurrentGood({ name: '', quantity: '', unit: 'kg', ghg_category: 'upstream' });
    setTransportLegs([]);
    setLocationSearch({ from: '', to: '' });
    setCalculationResult(null);
    setVehicleTypes([]);
    setVehicleTypeData(null);
    setSearchResults({ from: [], to: [] });
    setUseManualDistance(false);
    // Reset hierarchical vehicle selection
    setVehicleCategory('');
    setFuelType('');
    setSizeClass('');
    setLadenStatus('50_laden');
    setFlightType('');
    setIncludeRadiativeForcing(true);
    setShipType('');
    setShipSize('');
    setCurrentLeg({
      from_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
      to_location: { address: '', latitude: 0, longitude: 0, type: 'general' },
      transport_mode: '',
      vehicle_type: '',
      cost_type: 'total',
      cost_value: '',
      manual_distance: null,
      trips: '1'
    });
  };


  return (
    <div className={embedded ? '' : 'App min-h-screen'}>
      {loading && <LoadingSpinner />}

      {/* Enhanced Header — hidden when embedded inside AppLayout */}
      {!embedded && (
      <header className="bg-white/95 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-between items-center py-4">
            {/* Logo and Brand */}
            <div className="flex items-center gap-4">
              {/* Arantree Logo */}
              <div className="flex items-center gap-3 pr-4 border-r border-gray-200">
                <img
                  src="/assets/Arantree_logo_upscaled.png"
                  alt="Arantree Consulting Services"
                  className="h-16 w-auto hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* CarbonRoute Brand */}
              <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{
                  fontFamily: "'Poppins', sans-serif",
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 50%, #10b981 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  CarbonRoute
                </h1>
                <p className="text-xs text-gray-500 font-medium tracking-wide">
                  Calculate Scope 3 emissions from upstream & downstream logistics
                </p>
                <p className="text-xs text-gray-400">
                  Contact: <a href="mailto:info@arantree.com" className="text-teal-600 hover:text-teal-500">info@arantree.com</a>
                </p>
              </div>
            </div>

            {/* User Info and Controls */}
            <div className="flex items-center gap-4">
              {!user?.is_guest && <UsageWidget className="hidden md:block" />}

              {/* User Card — hidden for guests */}
              {!user?.is_guest && (
                <div className="flex items-center gap-3 bg-gradient-to-r from-gray-50 to-white rounded-xl px-4 py-2 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm shadow-inner">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-xs text-gray-500">Signed in as</p>
                    <p className="text-sm font-medium text-gray-800 max-w-32 truncate">{user?.email}</p>
                  </div>
                  {user?.is_admin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.location.href = '/admin'}
                      className="ml-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 transition-colors"
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Admin</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      logout();
                      window.location.href = '/login';
                    }}
                    className="ml-2 text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Quick Start Guide — hidden when embedded */}
      {!embedded && showGuide && (
        <div className="bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-b border-sky-100">
          <div className="container mx-auto px-4 max-w-7xl py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-sky-500/20">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">Quick Start Guide</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium text-gray-700">Enter Goods Details</p>
                        <p className="text-gray-500 text-xs">Add product name, quantity, and select GHG category</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium text-gray-700">Add Transport Legs</p>
                        <p className="text-gray-500 text-xs">Define origin, destination, transport mode & vehicle type</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium text-gray-700">Calculate Emissions</p>
                        <p className="text-gray-500 text-xs">Click calculate to get CO2 emissions and view analytics</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowGuide(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto py-6 px-4 max-w-7xl">

        {/* Enhanced Tabs for Calculator and Simulation */}
        <Tabs value={showBulkTab ? activeTab : 'calculator'} onValueChange={setActiveTab} className="w-full">
          {showBulkTab && (
          <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-gray-100 rounded-xl">
            <TabsTrigger
              value="bulk-upload"
              className="rounded-lg py-3 font-semibold text-sm transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-sky-600"
            >
              <Upload className="w-4 h-4 mr-2" />
              Batch Upload
            </TabsTrigger>
            <TabsTrigger
              value="calculator"
              className="rounded-lg py-3 font-semibold text-sm transition-all data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-sky-600"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Single Shipment
            </TabsTrigger>
          </TabsList>
          )}

          {/* Calculator Mode Tab */}
          <TabsContent value="calculator">
            {/* Centralised GHG Protocol category picker — drives the form body */}
            <div className="mb-5 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <Label className="text-sm font-medium text-gray-700 md:min-w-[220px]">
                  GHG Protocol Category
                </Label>
                <Select
                  value={String(scope3Category)}
                  onValueChange={(v) => {
                    const n = Number(v);
                    setScope3Category(n);
                    if (n === 4) handleGoodChange('ghg_category', 'upstream');
                    else if (n === 9) handleGoodChange('ghg_category', 'downstream');
                  }}
                >
                  <SelectTrigger className="md:max-w-md bg-white border-gray-300 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl">
                    <SelectItem value="4">Cat 4 — Upstream transport (shipments)</SelectItem>
                    <SelectItem value="9">Cat 9 — Downstream transport (shipments)</SelectItem>
                    <SelectItem value="6">Cat 6 — Business travel</SelectItem>
                    <SelectItem value="7">Cat 7 — Employee commuting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Cat 4/9 use the freight calculator (goods + transport legs). Cat 6/7 use the
                UK DESNZ 2026 business-travel / commute factors — enter a distance OR origin + destination
                and the backend geocodes for you.
              </p>
            </div>

            {/* Scope 3 Cat 6 / Cat 7 — single activity entry */}
            {(scope3Category === 6 || scope3Category === 7) && (
              <ScopeThreeActivityForm
                ghgCategory={scope3Category}
                prefill={scope3ChatPrefill}
                onSaved={() => {
                  setScope3ChatPrefill(null);
                  setScope3RefreshTick((t) => t + 1);
                }}
              />
            )}

            {/* Cat 4 / Cat 9 — existing shipment calculator */}
            {(scope3Category === 4 || scope3Category === 9) && (
            <>
            {/* Instructions */}
            <div className="mb-5 rounded-xl border border-border bg-card p-4">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-700">Single Shipment Calculator</span> — Enter goods details, add one or more transport legs (road, rail, air, or sea) with origin and destination, and calculate emissions for an individual shipment. Distances are computed automatically.
              </p>
            </div>
            {/* Three Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Column 1: Goods & Trip Details */}
              <Card data-testid="goods-section" className="pro-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                      <Package className="w-4 h-4" />
                    </div>
                    Goods & Trip Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-6">
                  <div>
                    <Label htmlFor="good-name" className="text-sm font-medium text-gray-700">Goods name</Label>
                    <Input
                      id="good-name"
                      value={currentGood.name}
                      onChange={(e) => handleGoodChange('name', e.target.value)}
                      placeholder=""
                      data-testid="good-name-input"
                      className="glass-input"
                    />
                  </div>

                  {/* GHG Category now driven by the top-level picker (Cat 4 or Cat 9) */}

                  <div>
                    <Label htmlFor="quantity" className="text-sm font-medium text-gray-700">Total quantity</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        id="quantity"
                        type="number"
                        value={currentGood.quantity}
                        onChange={(e) => handleGoodChange('quantity', e.target.value)}
                        placeholder=""
                        data-testid="good-quantity-input"
                        className="glass-input"
                      />
                      <Select value={currentGood.unit} onValueChange={(value) => handleGoodChange('unit', value)}>
                        <SelectTrigger data-testid="good-unit-select" className="bg-white border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="tons">tonnes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      Canonical mass: <span className="font-semibold">{currentGood.quantity ? (currentGood.unit === 'tons' ? currentGood.quantity : (currentGood.quantity / 1000).toFixed(2)) : 0} t</span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Column 2: Transportation Legs */}
              <Card data-testid="transport-leg-section" className="pro-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-800">Transportation Legs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Show current leg indicator */}
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                        {(() => {
                          const LegIcon = transportIcons[currentLeg.transport_mode] || Truck;
                          return <LegIcon className="h-4 w-4 text-gray-600" />;
                        })()}
                      </div>
                      <span className="font-medium text-gray-800">Leg {transportLegs.length + 1}</span>
                    </div>
                    {transportLegs.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-3"
                        onClick={resetCalculation}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Transport Mode Selection - Icon Based */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700 mb-3 block">Mode</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {transportModes.map((mode) => {
                        const Icon = transportIcons[mode.value];
                        const isSelected = currentLeg.transport_mode === mode.value;
                        return (
                          <button
                            key={mode.value}
                            type="button"
                            data-testid={`transport-mode-${mode.value}`}
                            onClick={() => handleLegChange('transport_mode', mode.value)}
                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${isSelected
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white hover:border-gray-400 text-gray-600'
                              }`}
                          >
                            <Icon className={`h-6 w-6 mb-1 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                            <span className="text-xs font-medium">{mode.label.split(' ')[0]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Vehicle Type Selection - Hierarchical based on transport mode */}
                  {currentLeg.transport_mode && vehicleTypeData && (
                    <div className="space-y-3">
                      {/* RAIL - Simple single option */}
                      {currentLeg.transport_mode === 'rail' && (
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <Label className="text-sm font-medium text-gray-700">Vehicle Type</Label>
                          <p className="text-sm text-gray-600 mt-1">Freight Train</p>
                        </div>
                      )}

                      {/* AIR - Flight type + Radiative Forcing toggle */}
                      {currentLeg.transport_mode === 'air' && (
                        <>
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Flight Type</Label>
                            <Select value={flightType} onValueChange={setFlightType}>
                              <SelectTrigger className="mt-1 bg-white border-gray-300">
                                <SelectValue placeholder="Select flight type" />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {vehicleTypeData.vehicle_types?.map((type) => (
                                  <SelectItem key={type.key} value={type.key}>{type.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm font-medium text-gray-700">Include Radiative Forcing</Label>
                              <div className="relative group">
                                <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity w-64 pointer-events-none z-50">
                                  Radiative Forcing (RF) accounts for the additional climate impact from aviation beyond just CO2, including contrails and water vapor at high altitudes. Including RF gives a more complete picture of aviation's climate impact.
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIncludeRadiativeForcing(!includeRadiativeForcing)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeRadiativeForcing ? 'bg-blue-600' : 'bg-gray-300'
                                }`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeRadiativeForcing ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                            </button>
                          </div>
                        </>
                      )}

                      {/* ROAD - Vehicle Category, Fuel Type (for Vans), Size Class, Laden Status (for HGV) */}
                      {currentLeg.transport_mode === 'road' && vehicleTypeData.hierarchical && (
                        <>
                          {/* Vehicle Category Selection */}
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Vehicle Category</Label>
                            <Select value={vehicleCategory} onValueChange={(value) => {
                              setVehicleCategory(value);
                              setFuelType('');
                              setSizeClass('');
                            }}>
                              <SelectTrigger className="mt-1 bg-white border-gray-300">
                                <SelectValue placeholder="Select vehicle category" />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {vehicleTypeData.vehicle_types?.map((cat) => (
                                  <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Fuel Type Selection (Vans only) - Radio buttons */}
                          {vehicleCategory === 'van' && (
                            <div>
                              <Label className="text-sm font-medium text-gray-700 mb-2 block">Fuel Type</Label>
                              <div className="flex gap-3">
                                {vehicleTypeData.vehicle_types?.find(c => c.key === 'van')?.fuel_types?.map((fuel) => (
                                  <label key={fuel.key} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      name="fuelType"
                                      value={fuel.key}
                                      checked={fuelType === fuel.key}
                                      onChange={(e) => {
                                        setFuelType(e.target.value);
                                        setSizeClass('');
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">{fuel.name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Size Class Selection (Vans) */}
                          {vehicleCategory === 'van' && fuelType && (
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Size Class</Label>
                              <Select value={sizeClass} onValueChange={setSizeClass}>
                                <SelectTrigger className="mt-1 bg-white border-gray-300">
                                  <SelectValue placeholder="Select size class" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {vehicleTypeData.vehicle_types?.find(c => c.key === 'van')?.fuel_types?.find(f => f.key === fuelType)?.sizes?.map((size) => (
                                    <SelectItem key={size.key} value={size.key}>{size.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Vehicle Size Selection (HGV/HGV Refrigerated) */}
                          {(vehicleCategory === 'hgv' || vehicleCategory === 'hgv_refrigerated') && (
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Vehicle Size</Label>
                              <Select value={sizeClass} onValueChange={setSizeClass}>
                                <SelectTrigger className="mt-1 bg-white border-gray-300">
                                  <SelectValue placeholder="Select vehicle size" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {vehicleTypeData.vehicle_types?.find(c => c.key === vehicleCategory)?.vehicle_sizes?.map((size) => (
                                    <SelectItem key={size.key} value={size.key}>{size.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Laden Status Selection (HGV/HGV Refrigerated) */}
                          {(vehicleCategory === 'hgv' || vehicleCategory === 'hgv_refrigerated') && sizeClass && (
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Label className="text-sm font-medium text-gray-700">Laden Status</Label>
                                <div className="relative group">
                                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity w-64 pointer-events-none z-50">
                                    Laden refers to how full the vehicle is loaded. 0% = empty, 50% = half loaded, 100% = fully loaded. Average uses typical loading patterns.
                                  </div>
                                </div>
                              </div>
                              <Select value={ladenStatus} onValueChange={setLadenStatus}>
                                <SelectTrigger className="mt-1 bg-white border-gray-300">
                                  <SelectValue placeholder="Select laden status" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  <SelectItem value="0_laden">0% Laden (Empty)</SelectItem>
                                  <SelectItem value="50_laden">50% Laden (Half Loaded)</SelectItem>
                                  <SelectItem value="100_laden">100% Laden (Fully Loaded)</SelectItem>
                                  <SelectItem value="average">Average</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </>
                      )}

                      {/* WATER - Ship Category, Ship Type, Size */}
                      {currentLeg.transport_mode === 'water' && vehicleTypeData.hierarchical && (
                        <>
                          {/* Ship Category Selection */}
                          <div>
                            <Label className="text-sm font-medium text-gray-700">Ship Category</Label>
                            <Select value={vehicleCategory} onValueChange={(value) => {
                              setVehicleCategory(value);
                              setShipType('');
                              setShipSize('');
                            }}>
                              <SelectTrigger className="mt-1 bg-white border-gray-300">
                                <SelectValue placeholder="Select ship category" />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {vehicleTypeData.vehicle_types?.map((cat) => (
                                  <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Ship Type Selection */}
                          {vehicleCategory && (
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Ship Type</Label>
                              <Select value={shipType} onValueChange={(value) => {
                                setShipType(value);
                                setShipSize('');
                              }}>
                                <SelectTrigger className="mt-1 bg-white border-gray-300">
                                  <SelectValue placeholder="Select ship type" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {vehicleTypeData.vehicle_types?.find(c => c.key === vehicleCategory)?.ship_types?.map((type) => (
                                    <SelectItem key={type.key} value={type.key}>{type.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Ship Size Selection */}
                          {vehicleCategory && shipType && (
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Ship Size</Label>
                              <Select value={shipSize} onValueChange={setShipSize}>
                                <SelectTrigger className="mt-1 bg-white border-gray-300">
                                  <SelectValue placeholder="Select ship size" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  {vehicleTypeData.vehicle_types?.find(c => c.key === vehicleCategory)?.ship_types?.find(t => t.key === shipType)?.sizes?.map((size) => (
                                    <SelectItem key={size.key} value={size.key}>{size.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Distance Input with Toggle - Moved Above Location Fields */}
                  {currentLeg.transport_mode && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-gray-700">Distance</Label>
                        <button
                          type="button"
                          onClick={() => {
                            setUseManualDistance(!useManualDistance);
                            if (!useManualDistance) {
                              handleLegChange('manual_distance', null);
                            }
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${useManualDistance
                              ? 'bg-blue-100 text-blue-700 border border-blue-300'
                              : 'bg-gray-100 text-gray-600 border border-gray-300'
                            }`}
                          data-testid="toggle-manual-distance"
                        >
                          Manual Calculate
                        </button>
                      </div>
                      {useManualDistance && (
                        <>
                        <Label htmlFor="manual-distance" className="sr-only">Distance (km)</Label>
                        <Input
                          id="manual-distance"
                          type="number"
                          value={currentLeg.manual_distance || ''}
                          onChange={(e) => handleLegChange('manual_distance', e.target.value || null)}
                          placeholder="Enter distance in km"
                          aria-label="Distance in kilometers"
                          data-testid="manual-distance-input"
                          className="bg-white border-gray-300"
                        />
                        </>
                      )}
                      {!useManualDistance && (
                        <div className="text-xs text-gray-500 italic">
                          Distance will be calculated automatically based on locations
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location Fields - Hidden when manual distance mode is active */}
                  {currentLeg.transport_mode && !useManualDistance && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative location-input-container">
                        <Label htmlFor="from-location" className="text-sm font-medium text-gray-700">From</Label>
                        <Input
                          id="from-location"
                          value={locationSearch.from}
                          onChange={(e) => handleLocationSearch('from', e.target.value)}
                          placeholder={currentLeg.transport_mode === 'air' ? 'Airport...' : 'Location...'}
                          className="glass-input"
                        />
                        {searchResults.from.length > 0 && (
                          <div className="search-dropdown location-dropdown-visible">
                            {searchResults.from.map((location, index) => (
                              <div
                                key={index}
                                className="search-option"
                                onClick={() => selectLocation('from', location)}
                                data-testid={`from-location-option-${index}`}
                              >
                                <div className="font-medium text-sm">{location.address}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="relative location-input-container">
                        <Label htmlFor="to-location" className="text-sm font-medium text-gray-700">To</Label>
                        <Input
                          id="to-location"
                          value={locationSearch.to}
                          onChange={(e) => handleLocationSearch('to', e.target.value)}
                          placeholder={currentLeg.transport_mode === 'air' ? 'Airport...' : 'Location...'}
                          data-testid="to-location-input"
                          className="mt-1 bg-white border-gray-300"
                        />
                        {searchResults.to.length > 0 && (
                          <div className="search-dropdown location-dropdown-visible">
                            {searchResults.to.map((location, index) => (
                              <div
                                key={index}
                                className="search-option"
                                onClick={() => selectLocation('to', location)}
                                data-testid={`to-location-option-${index}`}
                              >
                                <div className="font-medium text-sm">{location.address}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trips, Weight allocation, Cost */}
                  {currentLeg.transport_mode && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="trips" className="text-sm font-medium text-gray-700">Number of trips</Label>
                          <Input
                            id="trips"
                            type="number"
                            min="1"
                            max="1000"
                            step="1"
                            inputMode="numeric"
                            value={currentLeg.trips ?? '1'}
                            onChange={(e) => handleLegChange('trips', e.target.value)}
                            placeholder="1"
                            className="glass-input"
                          />
                        </div>

                      </div>

                      <div>
                        <Label htmlFor="cost-value" className="text-sm font-medium text-gray-700">Cost (INR)</Label>
                        <Input
                          id="cost-value"
                          type="number"
                          value={currentLeg.cost_value}
                          onChange={(e) => handleLegChange('cost_value', e.target.value)}
                          placeholder="0"
                          data-testid="cost-value-input"
                          className="glass-input"
                        />
                      </div>
                    </>
                  )}

                  {/* Add Transport Leg Button */}
                  <Button
                    onClick={addTransportLeg}
                    disabled={loading || addingLeg || transportLegs.length >= 5}
                    className="w-full glass-button-secondary"
                    data-testid="add-transport-leg-btn"
                    variant="secondary"
                  >
                    {addingLeg ? (
                      <>
                        <span className="inline-block w-4 h-4 mr-2 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></span>
                        Calculating...
                      </>
                    ) : (
                      'Save & Calculate Route'
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Column 3: Emissions */}
              <Card className="pro-card">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-green-600" />
                    Emissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Total legs:</span>
                      <span className="font-semibold text-gray-800">{transportLegs.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Mass (tonnes):</span>
                      <span className="font-semibold text-gray-800">
                        {currentGood.quantity ? (currentGood.unit === 'tons' ? currentGood.quantity : (currentGood.quantity / 1000).toFixed(2)) : 1}
                      </span>
                    </div>
                  </div>

                  {/* Calculate Emission Button */}
                  <Button
                    onClick={calculateShipment}
                    disabled={loading || !showCalculateButton}
                    className="w-full glass-button-primary"
                    data-testid="calculate-shipment-btn"
                  >
                    Calculate Emission
                  </Button>

                  {/* Result or Placeholder */}
                  {calculationResult ? (
                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Distance:</span>
                          <span className="font-semibold text-gray-800">{calculationResult.total_distance?.toFixed(1)} km</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Cost:</span>
                          <span className="font-semibold text-gray-800">₹{calculationResult.total_cost?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Emissions:</span>
                          <span className="font-semibold text-gray-800">{calculationResult.total_emissions?.toFixed(2)} kg CO₂</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 text-center">
                        No calculation yet. Enter details for each leg and press Calculate.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Transport Legs List (below the grid) */}
            {transportLegs.length > 0 && (
              <Card data-testid="current-legs-section" className="mt-6 pro-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-800">Added Transport Legs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {transportLegs.map((leg, index) => {
                    const Icon = transportIcons[leg.transport_mode];
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-gray-600" />
                          <div>
                            <div className="font-medium text-gray-800 text-sm">
                              {leg.from_location.address} → {leg.to_location.address}
                            </div>
                            <div className="text-xs text-gray-600">
                              {leg.vehicle_type} • {Number(leg.distance_km || 0).toFixed(1)} km{(leg.trips || 1) > 1 ? ` × ${leg.trips} trips` : ''} • ₹{leg.cost_value}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editTransportLeg(index)}
                            data-testid={`edit-leg-${index}`}
                            className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 h-8 px-2"
                            title="Edit leg"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTransportLeg(index)}
                            data-testid={`remove-leg-${index}`}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            title="Delete leg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
            </>
            )}
          </TabsContent>

          {/* Bulk Upload Tab */}
          {showBulkTab && (
          <TabsContent value="bulk-upload">
            {/* Same category picker so bulk upload knows which schema to show */}
            <div className="mb-5 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <Label className="text-sm font-medium text-gray-700 md:min-w-[220px]">
                  GHG Protocol Category
                </Label>
                <Select
                  value={String(scope3Category)}
                  onValueChange={(v) => {
                    const n = Number(v);
                    setScope3Category(n);
                    if (n === 4) handleGoodChange('ghg_category', 'upstream');
                    else if (n === 9) handleGoodChange('ghg_category', 'downstream');
                  }}
                >
                  <SelectTrigger className="md:max-w-md bg-white border-gray-300 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl">
                    <SelectItem value="4">Cat 4 — Upstream transport</SelectItem>
                    <SelectItem value="9">Cat 9 — Downstream transport</SelectItem>
                    <SelectItem value="6">Cat 6 — Business travel</SelectItem>
                    <SelectItem value="7">Cat 7 — Employee commuting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <AgentUpload
              onShipmentsSaved={loadShipments}
              onOpenInSingleShipment={loadLegsFromBatch}
              activityType={
                scope3Category === 6 ? 'business_travel' :
                scope3Category === 7 ? 'employee_commute' : 'freight'
              }
              onGoToDashboard={async () => {
                await loadShipments();
                setTimeout(() => {
                  document.getElementById('dashboard-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            />
          </TabsContent>
          )}

        </Tabs>

        {/* Dashboard Section - hidden when not requested (split into separate /dashboard route) */}
        {showDashboard && (
        <div id="dashboard-section" className="mt-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            <p className="text-sm text-gray-600 mt-1">Analytics and shipment history</p>
          </div>
          <DashboardContent
            shipments={shipments}
            onShipmentsChange={loadShipments}
            loadingShipments={loadingShipments}
            scope3RefreshTick={scope3RefreshTick}
            onScope3Changed={() => setScope3RefreshTick((t) => t + 1)}
          />
        </div>
        )}

      </div>
      <ChatBot
        onShipmentProposed={prefillFromChatProposal}
        onScope3Proposed={(activity) => {
          // Switch the calculator to the activity's category and prefill the form
          if (activity?.ghg_category && (activity.ghg_category === 6 || activity.ghg_category === 7)) {
            setScope3Category(activity.ghg_category);
          }
          setScope3ChatPrefill(activity);
          setActiveTab?.('calculator');
        }}
        category={scope3Category}
      />
    </div>
  );
};

// Custom scatter shape with size based on quantity
const CustomScatterShape = (props) => {
  const { cx, cy, fill, payload } = props;
  // Scale bubble size based on quantity (z value)
  // Min size: 6, Max size: 20, based on quantity
  const baseSize = 8;
  const scaleFactor = Math.min(Math.max(payload.z / 100, 0.5), 3); // Scale factor between 0.5 and 3
  const radius = baseSize * scaleFactor;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={fill}
      fillOpacity={0.7}
      stroke={fill}
      strokeWidth={2}
      strokeOpacity={1}
    />
  );
};

// Dashboard Component with Charts and Analytics

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Auth routes - public */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/verify-email-notice" element={<VerifyEmailNotice />} />
            <Route path="/auth/callback" element={<GoogleCallback />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/admin" element={
              <ProtectedRoute requireVerified={false}>
                <AdminDashboard />
              </ProtectedRoute>
            } />

            {/* Public commute survey — no auth */}
            <Route path="/commute-survey/:token" element={<CommuteSurveyPublic />} />

            {/* Scope 3 & Shanthi — auth required */}
            <Route path="/scope3" element={<Navigate to="/scope3/travel" replace />} />
            <Route path="/scope3/:tab" element={
              <ProtectedRoute>
                <ScopeThree />
              </ProtectedRoute>
            } />
            <Route path="/shanthi" element={
              <ProtectedRoute>
                <Shanthi />
              </ProtectedRoute>
            } />

            {/* Main routes — auth required */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/shipments/new" element={
              <ProtectedRoute>
                <ShipmentNew />
              </ProtectedRoute>
            } />
            <Route path="/shipments/bulk" element={
              <ProtectedRoute>
                <ShipmentsBulk />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;